import React from "react";
import StatusBadge from "../common/StatusBadge";
import RoomChip from "../common/RoomChip";
import { deviceDisplayName, deviceNameOnly } from "../../utils/deviceName";
import { summariseActions, dash } from "./scheduleFormat";

/**
 * The mobile app's on/off switch, rendered as a STATIC pill.
 *
 * ⚠️ Never an <input>. This dashboard is read-only: toggling a schedule here
 * would change when real lights switch, at a zman, on Shabbos.
 *
 * ⚠️ A draft renders NOTHING — parity with the app, which does not draw the
 * switch at all for drafts because a draft has no next run to toggle.
 */
export function ReadOnlyToggle({ status }) {
  if (status === "draft") return null;

  const isOn = status === "active";

  return (
    <span
      aria-disabled="true"
      title="Read-only — editing is not available in admin"
      className="inline-flex items-center gap-2 select-none"
    >
      <span
        className={`relative h-5 w-9 rounded-full transition-colors ${
          isOn ? "bg-emerald-500" : "bg-slate-300"
        }`}
      >
        <span
          className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${
            isOn ? "left-[18px]" : "left-0.5"
          }`}
        />
      </span>
      <span className="text-xs font-medium text-slate-500">
        {isOn ? "On" : status === "archived" ? "Archived" : "Paused"}
      </span>
    </span>
  );
}

/**
 * Up to two devices as "<name> in <Room>", then the ON/OFF/— tally.
 *
 * An action whose device is not in the index (removed from the account, or
 * isAdded:false) renders as its raw vendor id and is NOT clickable — better a
 * visibly unresolved device than a link to nothing or a silently dropped row.
 */
export function ScheduleDevices({ schedule, deviceIndex, basePath, navigate, max = 2 }) {
  const actions = schedule.actions || [];
  if (!actions.length) {
    return <span className="text-sm text-slate-400">No devices</span>;
  }

  const { total, label } = summariseActions(actions);

  return (
    <div className="min-w-[220px]">
      {actions.slice(0, max).map((action, i) => {
        const device = deviceIndex?.byVendorId?.[action.deviceId];
        const room = device?.roomId ?? null;

        return (
          <div key={`${action.deviceId}-${i}`} className="mb-1 flex flex-wrap items-center gap-1.5">
            {device ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`${basePath}/devices/${device._id}`);
                }}
                className="text-sm font-medium text-slate-700 hover:text-indigo-600 hover:underline"
              >
                {deviceDisplayName(device)}
              </button>
            ) : (
              <span
                title="This device is no longer in the user's account"
                className="font-mono text-xs text-slate-400"
              >
                {action.deviceId}
              </span>
            )}

            <span className="text-xs text-slate-400">in</span>
            <RoomChip
              room={room}
              onClick={room?._id ? () => navigate(`${basePath}/rooms/${room._id}`) : undefined}
            />
            <StatusBadge kind="powerState" value={action.deviceOn} unknownLabel="—" />
          </div>
        );
      })}

      {total > max && (
        <p className="mt-1 text-[11px] text-slate-400">{label}</p>
      )}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-slate-400">{label}</dt>
      <dd className="text-right">{children}</dd>
    </div>
  );
}

/** The per-action detail shown in the schedule Detail modal. */
export function ScheduleActionsPanel({ schedule, deviceIndex }) {
  const actions = schedule.actions || [];
  if (!actions.length) {
    return <p className="text-sm text-slate-500">This schedule has no actions.</p>;
  }

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
      {actions.map((action, i) => {
        const device = deviceIndex?.byVendorId?.[action.deviceId];
        // The device row is the only place a name lives (the user's alias, or
        // the provider's `meta.alias`). With no name the card shows the vendor
        // id ONCE, never the id as a "name" and the id again under it.
        //
        // "Not found" is said only when the row is really missing from the
        // index — and not as "removed": the index is also empty while it is
        // still loading or if it failed.
        const name = device ? deviceNameOnly(device) : action.deviceName || null;

        return (
          <div
            key={`${action.deviceId}-${i}`}
            className="rounded-lg border border-slate-200 bg-white p-4"
          >
            <div className="flex items-start justify-between gap-2">
              {/* min-w-0 + break-all: a 40-character vendor id has no spaces,
                  so without them it overflows the card. */}
              <div className="min-w-0">
                {name ? (
                  <>
                    <p className="break-words font-semibold text-slate-800">{name}</p>
                    <p className="break-all font-mono text-[11px] text-slate-400">
                      {action.deviceId}
                    </p>
                  </>
                ) : (
                  <p
                    title={device ? "This device has no name" : "Not found in this user's device list"}
                    className="break-all font-mono text-xs font-semibold text-slate-500"
                  >
                    {action.deviceId}
                  </p>
                )}
              </div>
              <StatusBadge kind="powerState" value={action.deviceOn} unknownLabel="—" />
            </div>

            <dl className="mt-3 space-y-1 text-xs text-slate-600">
              <Row label="Brand">
                <span className="capitalize">{dash(action.brand)}</span>
              </Row>

              {/* Brand decides WHICH rows apply: brightness is meaningless on a
                  thermostat and setpoints are meaningless on a plug. Within the
                  applicable set every row renders, "—" included, so two devices
                  of the same brand always show the same shape. */}
              {action.brand === "honeywell" ? (
                <>
                  <Row label="Mode">
                    <span className="capitalize">{dash(action.mode)}</span>
                  </Row>
                  <Row label="Heat to">
                    {action.heatSetpoint != null ? `${action.heatSetpoint}°` : "—"}
                  </Row>
                  <Row label="Cool to">
                    {action.coolSetpoint != null ? `${action.coolSetpoint}°` : "—"}
                  </Row>
                  <Row label="Fan">
                    <span className="capitalize">{dash(action.fanMode)}</span>
                  </Row>
                </>
              ) : (
                <Row label="Brightness">
                  {action.brightnessLevel != null ? `${action.brightnessLevel}%` : "—"}
                </Row>
              )}

              <Row label="Supported">
                {/* Unset is not "supported" — say so rather than guessing Yes. */}
                {action.supported === null || action.supported === undefined
                  ? "—"
                  : action.supported
                    ? "Yes"
                    : "No"}
              </Row>
            </dl>

            {(action.errorCode || action.remark) && (
              <p className="mt-3 rounded-md bg-red-50 px-2 py-1.5 text-[11px] text-red-700">
                {action.errorCode ? `${action.errorCode}: ` : ""}
                {action.remark || "Reported an error"}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
